import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { UserTestDto } from "~/data/user-test/user-test.dto";

import whistleSound from "~/ui/shared/assets/sounds/whistle.mp3";
import { updateUserTest } from "~/data/user-test/user-test.service";

import { Dialog } from "../shared/dialog";
import { Button } from "../shared/button";
import { IconButton } from "../shared/icon-button";

interface TimerProps {
  data: UserTestDto;
}
export function Timer({ data }: TimerProps) {
  const start = data.started_at;
  const limit = data.time_to_complete_in_minutes ?? 10;

  const limitInMs = limit * 60 * 1000;
  const [now, setNow] = useState(Date.now());
  const totalSecondsLeft = Math.max(
    ((start ? Date.parse(start) : Date.now()) + limitInMs - now) / 1000,
    0
  );
  const timeOver = totalSecondsLeft === 0;

  useEffect(() => {
    if (!timeOver) {
      const id = setInterval(() => setNow(Date.now()), 500);
      return () => clearInterval(id);
    }
  }, [timeOver]);

  const minutesString = String(Math.floor(totalSecondsLeft / 60)).padStart(
    2,
    "0"
  );
  const secondsString = String(Math.floor(totalSecondsLeft % 60)).padStart(
    2,
    "0"
  );

  const params = useParams();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const updateTimeMutation = useMutation({
    mutationFn: (time: number) =>
      updateUserTest(params.id!, { time_to_complete_in_minutes: time }),
    onSuccess: (partial) =>
      queryClient.setQueryData<UserTestDto>(
        ["user_test", params.id],
        (data) => data && { ...data, ...partial }
      ),
  });
  const { mutate: finishUserTest } = useMutation({
    mutationFn: () =>
      updateUserTest(params.id!, { finished_at: new Date().toISOString() }),
    onSuccess: (partial) =>
      queryClient.setQueryData<UserTestDto>(
        ["user_test", params.id],
        (data) => data && { ...data, ...partial }
      ),
  });
  useEffect(() => {
    if (timeOver && !data.finished_at) {
      // Audio effect on finish test
      const audio = new Audio(whistleSound);
      audio.play();

      finishUserTest(void 0);
    }
  }, [data.finished_at, timeOver, finishUserTest]);

  return (
    <div className="bg-white shadow-card h-12 px-3 rounded-3xl flex items-center justify-between gap-2">
      <IconButton
        type="button"
        className="bg-gray-50 p-1 rounded-md text-green-500"
        onClick={() => setOpen(true)}
        icon={
          <svg
            width="34"
            height="34"
            viewBox="0 0 34 34"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M33.6673 17C33.6673 7.76665 26.234 0.333313 17.0007 0.333313C15.0507 0.333313 13.1673 0.64998 11.3673 1.26665L12.534 4.49998C13.9507 3.93331 15.434 3.61665 17.0007 3.61665C24.3507 3.61665 30.384 9.64998 30.384 17C30.384 24.35 24.3507 30.3833 17.0007 30.3833C9.65065 30.3833 3.61732 24.35 3.61732 17C3.61732 15.4333 3.93398 13.8666 4.50065 12.4666L1.26732 11.3666C0.650651 13.1666 0.333984 15.05 0.333984 17C0.333984 26.2333 7.76732 33.6666 17.0007 33.6666C26.234 33.6666 33.6673 26.2333 33.6673 17ZM6.11732 3.61665C7.53398 3.61665 8.66732 4.79998 8.66732 6.11665C8.66732 7.53331 7.53398 8.66665 6.11732 8.66665C4.80065 8.66665 3.61732 7.53331 3.61732 6.11665C3.61732 4.79998 4.80065 3.61665 6.11732 3.61665ZM27.0007 17C27.0007 11.45 22.5507 6.99998 17.0007 6.99998C11.4507 6.99998 7.00065 11.45 7.00065 17C7.00065 22.55 11.4507 27 17.0007 27C22.5507 27 27.0007 22.55 27.0007 17ZM22.0007 17L13.6673 22V12"
              fill="#1AAF50"
            />
          </svg>
        }
      />

      <span className="font-semibold font-alternative text-[22px] w-[74px]">
        {minutesString} : {secondsString}
      </span>

      {!start && (
        <Dialog open={open} onOpenChange={setOpen}>
          <Dialog.Content className="bg-white shadow-card max-w-md">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const value = Number(
                  new FormData(e.currentTarget).get(
                    "time_to_complete_in_minutes"
                  )
                );
                updateTimeMutation.mutate(value, {
                  onSuccess: () => setOpen(false),
                });
              }}
            >
              <h3 className="text-2xl font-semibold text-black mb-3">
                Tempo da Estação
              </h3>
              <select
                name="time_to_complete_in_minutes"
                className="w-full border h-12 px-2 rounded-lg mb-5"
              >
                <option value="10">10 Minutos</option>
                <option value="9">9 Minutos</option>
                <option value="8">8 Minutos</option>
                <option value="7">7 Minutos</option>
                <option value="6">6 Minutos</option>
              </select>
              <Button
                loading={updateTimeMutation.isPending}
                variant="primary-1"
                className="h-14 w-full"
              >
                Aplicar
              </Button>
            </form>
          </Dialog.Content>
        </Dialog>
      )}
    </div>
  );
}
